import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  addPrazoVendaItemAdmin,
  createPrazoClienteAdmin,
  createPrazoPagamentoAdmin,
  createPrazoVendaAdmin,
  deletePrazoVendaAdmin,
  getCategorias,
  getPrazoClientesAdmin,
  updatePrazoClienteAdmin,
  getPrazoVendasAdmin,
  getProdutos,
  reopenPrazoVendaAdmin
} from '../services/api';
import { getImageUrl } from '../utils/imageUrl';

const METODOS = ['pix', 'dinheiro', 'debito', 'credito'];

const formatCurrencyBRL = (value) => `R$ ${Number(value || 0).toFixed(2)}`;

/** Ordem alfabética para nomes de produto (pt-BR, acentos, números no texto). */
function ordenarNomeProdutoPt(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'pt-BR', { sensitivity: 'base', numeric: true });
}

const NOME_FANTASIA_LOJA = 'Loucas por Beleza';

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function computeFinanceiroVenda(venda) {
  const somaItensVenda = (venda.itens || []).reduce((s, i) => s + Number(i.subtotal || 0), 0);
  const bruto =
    venda.subtotal_bruto != null && venda.subtotal_bruto !== ''
      ? Number(venda.subtotal_bruto)
      : somaItensVenda;
  const desc = Number(venda.valor_desconto ?? venda.valorDesconto ?? 0);
  const total = Number(venda.total || 0);
  return { bruto, desc, total };
}

/** Logotipo vetorial simples para impressão (não depende de arquivo no servidor). */
const LOGO_SVG_NOTA = `<svg width="56" height="56" viewBox="0 0 100 100" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
  <circle cx="50" cy="50" r="46" fill="#fce4ec" stroke="#ad1457" stroke-width="3"/>
  <path d="M35 38c0-8 6-14 15-14s15 6 15 14v8H35v-8z" fill="#fff" stroke="#880e4f" stroke-width="2"/>
  <ellipse cx="50" cy="62" rx="18" ry="12" fill="#ad1457" opacity="0.9"/>
</svg>`;

function LogoNotaMarca() {
  return (
    <svg className="aprazo-nota-logo-svg" width="56" height="56" viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="46" fill="#fce4ec" stroke="#ad1457" strokeWidth="3" />
      <path
        d="M35 38c0-8 6-14 15-14s15 6 15 14v8H35v-8z"
        fill="#fff"
        stroke="#880e4f"
        strokeWidth="2"
      />
      <ellipse cx="50" cy="62" rx="18" ry="12" fill="#ad1457" opacity="0.9" />
    </svg>
  );
}

function NotaPreviewCorpo({ venda }) {
  const { bruto, desc, total } = computeFinanceiroVenda(venda);
  const dataCompra = venda.created_at
    ? new Date(venda.created_at).toLocaleString('pt-BR', { dateStyle: 'long', timeStyle: 'short' })
    : '—';
  const itens = (venda.itens || [])
    .slice()
    .sort((x, y) => ordenarNomeProdutoPt(x.nome_produto, y.nome_produto));
  return (
    <div className="aprazo-nota-documento vendas-analytics-section relatorio-preview-section">
      <div className="aprazo-nota-cabecalho">
        <LogoNotaMarca />
        <div>
          <h3 className="aprazo-nota-marca">{NOME_FANTASIA_LOJA}</h3>
          <p className="aprazo-nota-nome-fantasia">
            <strong>Nome fantasia:</strong> {NOME_FANTASIA_LOJA}
          </p>
        </div>
      </div>
      <p className="aprazo-nota-meta">
        <strong>Nota de venda a prazo</strong> · Venda #{venda.id}
      </p>
      <p className="aprazo-nota-meta">
        <strong>Cliente:</strong> {venda.cliente_nome || '—'}
      </p>
      <p className="aprazo-nota-meta">
        <strong>Data da compra:</strong> {dataCompra}
      </p>
      <div className="relatorio-tabela-wrap">
        <table className="relatorio-tabela aprazo-nota-tabela">
          <thead>
            <tr>
              <th>Produto</th>
              <th className="aprazo-nota-col-num">Qtd</th>
              <th className="aprazo-nota-col-num">Unit.</th>
              <th className="aprazo-nota-col-num">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {itens.length === 0 ? (
              <tr>
                <td colSpan={4}>Nenhum item</td>
              </tr>
            ) : (
              itens.map((item) => (
                <tr key={item.id ?? `${item.produto_id}-${item.nome_produto}`}>
                  <td>{item.nome_produto}</td>
                  <td className="aprazo-nota-col-num">{Number(item.quantidade || 0)}</td>
                  <td className="aprazo-nota-col-num">{formatCurrencyBRL(item.preco_unitario || 0)}</td>
                  <td className="aprazo-nota-col-num">{formatCurrencyBRL(item.subtotal || 0)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="aprazo-nota-totais">
        <div>
          <span>Total sem desconto</span>
          <span>{formatCurrencyBRL(bruto)}</span>
        </div>
        <div>
          <span>Desconto</span>
          <span>−{formatCurrencyBRL(desc)}</span>
        </div>
        <div className="aprazo-nota-total-final">
          <span>Total com desconto</span>
          <span>{formatCurrencyBRL(total)}</span>
        </div>
      </div>
    </div>
  );
}

function montarHtmlDocumentoNotaVenda(venda) {
  const { bruto, desc, total } = computeFinanceiroVenda(venda);
  const dataCompra = venda.created_at
    ? new Date(venda.created_at).toLocaleString('pt-BR', { dateStyle: 'long', timeStyle: 'short' })
    : '—';
  const cliente = escapeHtml(venda.cliente_nome || '—');
  const itens = (venda.itens || [])
    .slice()
    .sort((x, y) => ordenarNomeProdutoPt(x.nome_produto, y.nome_produto));
  const linhas = itens
    .map(
      (item) => `<tr>
      <td>${escapeHtml(item.nome_produto)}</td>
      <td class="num">${Number(item.quantidade || 0)}</td>
      <td class="num">${formatCurrencyBRL(item.preco_unitario || 0)}</td>
      <td class="num">${formatCurrencyBRL(item.subtotal || 0)}</td>
    </tr>`
    )
    .join('');
  const nomeFantasiaEsc = escapeHtml(NOME_FANTASIA_LOJA);
  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8"/>
<title>Nota — ${nomeFantasiaEsc} — Venda #${venda.id}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, "Segoe UI", sans-serif; margin: 24px; color: #222; }
  .cabecalho { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; border-bottom: 2px solid #ad1457; padding-bottom: 16px; }
  .cabecalho h1 { margin: 0; font-size: 1.45rem; color: #880e4f; }
  .nome-fantasia { margin: 6px 0 0; font-size: 0.92rem; color: #555; }
  .meta { margin: 10px 0; font-size: 0.95rem; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th, td { border-bottom: 1px solid #e0e0e0; padding: 8px 6px; text-align: left; }
  th { background: #fce4ec; color: #4a148c; font-size: 0.88rem; }
  .num { text-align: right; }
  .totais { margin-top: 22px; max-width: 340px; margin-left: auto; }
  .totais div { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px solid #eee; font-size: 0.95rem; }
  .totais .destaque { font-weight: 700; font-size: 1.08rem; border-top: 2px solid #ad1457; margin-top: 8px; padding-top: 10px; border-bottom: none; }
  @media print { body { margin: 12mm; } }
</style>
</head>
<body>
  <div class="cabecalho">
    ${LOGO_SVG_NOTA}
    <div>
      <h1>${nomeFantasiaEsc}</h1>
      <p class="nome-fantasia"><strong>Nome fantasia:</strong> ${nomeFantasiaEsc}</p>
    </div>
  </div>
  <p class="meta"><strong>Nota de venda a prazo</strong> · Venda #${venda.id}</p>
  <p class="meta"><strong>Cliente:</strong> ${cliente}</p>
  <p class="meta"><strong>Data da compra:</strong> ${escapeHtml(dataCompra)}</p>
  <table>
    <thead><tr><th>Produto</th><th class="num">Qtd</th><th class="num">Unit.</th><th class="num">Subtotal</th></tr></thead>
    <tbody>${linhas || '<tr><td colspan="4">Nenhum item</td></tr>'}</tbody>
  </table>
  <div class="totais">
    <div><span>Total sem desconto</span><span>${formatCurrencyBRL(bruto)}</span></div>
    <div><span>Desconto</span><span>−${formatCurrencyBRL(desc)}</span></div>
    <div class="destaque"><span>Total com desconto</span><span>${formatCurrencyBRL(total)}</span></div>
  </div>
</body>
</html>`;
  return html;
}

function imprimirNotaVendaJanela(venda) {
  const html = montarHtmlDocumentoNotaVenda(venda);
  const w = window.open('', '_blank', 'width=1024,height=768');
  if (!w) {
    toast.error('Permita pop-ups para imprimir a nota.');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => {
    try {
      w.print();
    } catch {
      /* ignore */
    }
  }, 200);
}

function toastErroApi(err, fallback) {
  const d = err?.response?.data;
  const base = d?.error || err?.message || fallback;
  if (Number(err?.response?.status) === 404 && d) {
    const detalhe = [d.method, d.originalUrl || d.path].filter(Boolean).join(' ');
    toast.error(
      detalhe
        ? `${base} — ${detalhe}. Se usa Nginx/Apache, confira se /api encaminha para a mesma porta do Node (PM2 costuma usar 3105).`
        : base
    );
    return;
  }
  toast.error(base);
}

export default function AreaPrazoAdmin() {
  const [clientes, setClientes] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [clienteSelecionadoId, setClienteSelecionadoId] = useState(null);
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [buscaProduto, setBuscaProduto] = useState('');
  const [qtyMap, setQtyMap] = useState({});
  const [carrinho, setCarrinho] = useState([]);
  const [metodoVenda, setMetodoVenda] = useState('prazo');
  const [descontoModo, setDescontoModo] = useState('dinheiro');
  const [descontoCampo, setDescontoCampo] = useState('');
  const [pedidoFechado, setPedidoFechado] = useState(false);
  const [pagamentoForm, setPagamentoForm] = useState({});
  const [addItemForm, setAddItemForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiRouteMissing, setApiRouteMissing] = useState(false);
  const [editingClienteId, setEditingClienteId] = useState(null);
  const [notaPreviewVenda, setNotaPreviewVenda] = useState(null);

  const [novoCliente, setNovoCliente] = useState({
    nome: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    cel: ''
  });

  const clienteSelecionado = useMemo(
    () => clientes.find((c) => Number(c.id) === Number(clienteSelecionadoId)) || null,
    [clientes, clienteSelecionadoId]
  );

  const produtosFiltrados = useMemo(() => {
    const list = produtos.filter((p) => {
      if (!p.ativo) return false;
      if (categoriaFiltro && String(p.categoria_id) !== String(categoriaFiltro)) return false;
      if (buscaProduto.trim()) {
        const t = buscaProduto.toLowerCase();
        if (!String(p.nome || '').toLowerCase().includes(t)) return false;
      }
      return true;
    });
    return list.sort((a, b) => ordenarNomeProdutoPt(a.nome, b.nome));
  }, [produtos, categoriaFiltro, buscaProduto]);

  const totalCarrinho = useMemo(
    () => carrinho.reduce((s, item) => s + Number(item.preco_unitario || 0) * Number(item.quantidade || 0), 0),
    [carrinho]
  );

  const subtotalBruto = totalCarrinho;

  const { valorDescontoCalculado, totalComDesconto } = useMemo(() => {
    const bruto = Math.max(0, Math.round(subtotalBruto * 100) / 100);
    const raw = String(descontoCampo || '').replace(',', '.').trim();
    const param = Math.max(0, Number(raw) || 0);
    if (bruto <= 0) return { valorDescontoCalculado: 0, totalComDesconto: 0 };
    let desc = 0;
    if (descontoModo === 'percentual') {
      const p = Math.min(100, param);
      desc = Math.round((bruto * (p / 100)) * 100) / 100;
    } else {
      desc = Math.min(bruto, Math.round(param * 100) / 100);
    }
    const liq = Math.max(0, Math.round((bruto - desc) * 100) / 100);
    return { valorDescontoCalculado: desc, totalComDesconto: liq };
  }, [subtotalBruto, descontoCampo, descontoModo]);

  const carrinhoComIndicesOrdenados = useMemo(
    () =>
      carrinho
        .map((item, idx) => ({ item, idx }))
        .sort((a, b) => ordenarNomeProdutoPt(a.item.nome_produto, b.item.nome_produto)),
    [carrinho]
  );

  const vendasCliente = useMemo(
    () => vendas.filter((v) => Number(v.cliente_aprazo_id) === Number(clienteSelecionadoId)),
    [vendas, clienteSelecionadoId]
  );

  const vendaNotaExibida = useMemo(() => {
    if (!notaPreviewVenda) return null;
    const fresh = vendas.find((v) => Number(v.id) === Number(notaPreviewVenda.id));
    return fresh || notaPreviewVenda;
  }, [notaPreviewVenda, vendas]);

  const produtosAtivosOrdenados = useMemo(
    () => [...produtos].filter((p) => p.ativo).sort((a, b) => ordenarNomeProdutoPt(a.nome, b.nome)),
    [produtos]
  );

  const loadBase = async () => {
    setLoading(true);
    setApiRouteMissing(false);
    try {
      const [clientesRes, vendasRes, produtosRes, categoriasRes] = await Promise.all([
        getPrazoClientesAdmin(),
        getPrazoVendasAdmin(),
        getProdutos(),
        getCategorias()
      ]);
      setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
      setVendas(Array.isArray(vendasRes.data) ? vendasRes.data : []);
      setProdutos(Array.isArray(produtosRes.data) ? produtosRes.data : []);
      setCategorias(Array.isArray(categoriasRes.data) ? categoriasRes.data : []);
    } catch (err) {
      const is404 = Number(err?.response?.status) === 404;
      setApiRouteMissing(is404);
      if (is404) {
        toast.error('Rota "A Prazo" não encontrada. Reinicie o backend para carregar a nova rota.');
      } else {
        toast.error(err.response?.data?.error || 'Erro ao carregar A Prazo.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBase();
  }, []);

  useEffect(() => {
    if (notaPreviewVenda) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [notaPreviewVenda]);

  useEffect(() => {
    if (carrinho.length === 0) setPedidoFechado(false);
  }, [carrinho.length]);

  const handleCadastrarCliente = async (e) => {
    e.preventDefault();
    if (!novoCliente.nome.trim()) {
      toast.error('Informe o nome do cliente.');
      return;
    }
    setSaving(true);
    try {
      if (editingClienteId) {
        const res = await updatePrazoClienteAdmin(editingClienteId, novoCliente);
        const cliente = res.data;
        setClientes((prev) => prev.map((c) => (Number(c.id) === Number(cliente.id) ? cliente : c)));
        setClienteSelecionadoId(cliente.id);
        setEditingClienteId(null);
        setNovoCliente({ nome: '', endereco: '', numero: '', bairro: '', cidade: '', cel: '' });
        toast.success('Cadastro atualizado com sucesso.');
      } else {
        const res = await createPrazoClienteAdmin(novoCliente);
        const cliente = res.data;
        setClientes((prev) => [cliente, ...prev]);
        setClienteSelecionadoId(cliente.id);
        setNovoCliente({ nome: '', endereco: '', numero: '', bairro: '', cidade: '', cel: '' });
        toast.success('Cliente a prazo cadastrado.');
      }
    } catch (err) {
      const apiError = err.response?.data?.error;
      const apiPath = err.response?.data?.path;
      if (apiPath) {
        toast.error(`${apiError || 'Erro ao salvar cliente.'} (${apiPath})`);
      } else {
        toast.error(apiError || 'Erro ao salvar cliente.');
      }
    } finally {
      setSaving(false);
    }
  };

  const iniciarEdicaoCliente = (cliente) => {
    setEditingClienteId(cliente.id);
    setNovoCliente({
      nome: cliente.nome || '',
      endereco: cliente.endereco || '',
      numero: cliente.numero || '',
      bairro: cliente.bairro || '',
      cidade: cliente.cidade || '',
      cel: cliente.cel || ''
    });
  };

  const cancelarEdicaoCliente = () => {
    setEditingClienteId(null);
    setNovoCliente({ nome: '', endereco: '', numero: '', bairro: '', cidade: '', cel: '' });
  };

  const addCarrinho = (produto) => {
    if (pedidoFechado) return;
    const qty = Math.max(1, parseInt(qtyMap[String(produto.id)], 10) || 1);
    const preco = Number(produto.preco || 0);
    setCarrinho((prev) => {
      const idx = prev.findIndex((i) => Number(i.produto_id) === Number(produto.id));
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantidade: Number(next[idx].quantidade || 0) + qty };
        return next;
      }
      return [
        ...prev,
        { produto_id: produto.id, nome_produto: produto.nome, quantidade: qty, preco_unitario: preco, foto: produto.foto }
      ];
    });
    setQtyMap((prev) => ({ ...prev, [String(produto.id)]: 1 }));
  };

  const updateQtdCarrinho = (index, value) => {
    const qty = Math.max(1, parseInt(value, 10) || 1);
    setCarrinho((prev) => prev.map((item, i) => (i === index ? { ...item, quantidade: qty } : item)));
  };

  const removerCarrinho = (index) => setCarrinho((prev) => prev.filter((_, i) => i !== index));

  const handleCriarVenda = async () => {
    if (!clienteSelecionadoId) {
      toast.error('Selecione um cliente para registrar a venda.');
      return;
    }
    if (!carrinho.length) {
      toast.error('Adicione produtos ao carrinho.');
      return;
    }
    if (totalComDesconto < 0.01) {
      toast.error('O total após desconto deve ser pelo menos R$ 0,01.');
      return;
    }
    setSaving(true);
    try {
      const rawDesc = String(descontoCampo || '').replace(',', '.').trim();
      const payload = {
        cliente_id: clienteSelecionadoId,
        forma_pagamento: metodoVenda,
        itens: carrinho.map((i) => ({ produto_id: i.produto_id, quantidade: i.quantidade })),
        desconto_modo: descontoModo,
        desconto_valor: rawDesc === '' ? 0 : Number(rawDesc) || 0,
        pedido_fechado: true
      };
      const res = await createPrazoVendaAdmin(payload);
      const venda = res.data;
      setVendas((prev) => [venda, ...prev]);
      setCarrinho([]);
      setPedidoFechado(false);
      setDescontoCampo('');
      setDescontoModo('dinheiro');
      toast.success('Venda a prazo criada com sucesso.');
    } catch (err) {
      toastErroApi(err, 'Erro ao criar venda a prazo.');
    } finally {
      setSaving(false);
    }
  };

  const handleExcluirVenda = async (vendaId) => {
    if (!window.confirm('Excluir esta venda? O estoque dos produtos será devolvido. Esta ação não pode ser desfeita.')) {
      return;
    }
    setSaving(true);
    try {
      await deletePrazoVendaAdmin(vendaId);
      setVendas((prev) => prev.filter((v) => Number(v.id) !== Number(vendaId)));
      setAddItemForm((prev) => {
        const next = { ...prev };
        delete next[String(vendaId)];
        return next;
      });
      toast.success('Venda excluída.');
    } catch (err) {
      toastErroApi(err, 'Erro ao excluir venda.');
    } finally {
      setSaving(false);
    }
  };

  const handleReabrirVenda = async (vendaId) => {
    setSaving(true);
    try {
      const res = await reopenPrazoVendaAdmin(vendaId);
      const vendaAtualizada = res.data;
      setVendas((prev) => prev.map((v) => (Number(v.id) === Number(vendaId) ? vendaAtualizada : v)));
      toast.success('Venda reaberta. Você pode lançar pagamentos ou incluir itens.');
    } catch (err) {
      toastErroApi(err, 'Erro ao reabrir venda.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItemNaVenda = async (vendaId) => {
    const raw = addItemForm[String(vendaId)] || { produto_id: '', quantidade: 1 };
    const produtoId = Number(raw.produto_id);
    const quantidade = Math.max(1, parseInt(raw.quantidade, 10) || 1);
    if (!produtoId) {
      toast.error('Selecione um produto para adicionar.');
      return;
    }
    setSaving(true);
    try {
      const res = await addPrazoVendaItemAdmin(vendaId, { produto_id: produtoId, quantidade });
      const vendaAtualizada = res.data;
      setVendas((prev) => prev.map((v) => (Number(v.id) === Number(vendaId) ? vendaAtualizada : v)));
      setAddItemForm((prev) => ({
        ...prev,
        [String(vendaId)]: { produto_id: '', quantidade: 1 }
      }));
      toast.success('Item adicionado à venda.');
    } catch (err) {
      toastErroApi(err, 'Erro ao adicionar item.');
    } finally {
      setSaving(false);
    }
  };

  const handleLancarPagamento = async (vendaId) => {
    const form = pagamentoForm[String(vendaId)] || {};
    const valor = Number(form.valor || 0);
    if (valor <= 0) {
      toast.error('Informe um valor válido para pagamento.');
      return;
    }
    setSaving(true);
    try {
      const res = await createPrazoPagamentoAdmin(vendaId, {
        valor,
        metodo: form.metodo || 'pix',
        data_pagamento: form.data_pagamento || new Date().toISOString().slice(0, 10),
        observacao: form.observacao || ''
      });
      const vendaAtualizada = res.data;
      setVendas((prev) => prev.map((v) => (Number(v.id) === Number(vendaId) ? vendaAtualizada : v)));
      setPagamentoForm((prev) => ({
        ...prev,
        [String(vendaId)]: { valor: '', metodo: 'pix', data_pagamento: new Date().toISOString().slice(0, 10), observacao: '' }
      }));
      toast.success('Pagamento lançado e saldo atualizado.');
    } catch (err) {
      toastErroApi(err, 'Erro ao lançar pagamento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aprazo-container">
      <h2>A Prazo</h2>
      <p className="venda-step-desc">Cadastre clientes, monte o carrinho da venda e faça pagamentos parciais até quitar.</p>
      {apiRouteMissing && (
        <div className="aprazo-alert" role="alert">
          A rota da API para A Prazo não está ativa. Reinicie o backend para aplicar a atualização.
        </div>
      )}

      {loading ? (
        <p>Carregando área a prazo...</p>
      ) : notaPreviewVenda && vendaNotaExibida ? (
        <div className="aprazo-nota-tela">
          <div className="vendas-header-with-btn aprazo-nota-tela-toolbar">
            <h3 className="aprazo-nota-tela-title">
              Prévia da nota · Venda #{vendaNotaExibida.id}
            </h3>
            <div className="vendas-header-actions">
              <button type="button" className="btn-nova-venda" onClick={() => setNotaPreviewVenda(null)}>
                Voltar
              </button>
              <button
                type="button"
                className="btn-imprimir-relatorio"
                onClick={() => imprimirNotaVendaJanela(vendaNotaExibida)}
              >
                Imprimir nota
              </button>
            </div>
          </div>
          <NotaPreviewCorpo venda={vendaNotaExibida} />
        </div>
      ) : (
        <>
          <div className="aprazo-grid">
            <div className="aprazo-card">
              <h3>{editingClienteId ? 'Editar cliente a prazo' : 'Cadastrar cliente a prazo'}</h3>
              <form onSubmit={handleCadastrarCliente} className="aprazo-form">
                <input placeholder="Nome" value={novoCliente.nome} onChange={(e) => setNovoCliente((p) => ({ ...p, nome: e.target.value }))} />
                <input placeholder="Endereço" value={novoCliente.endereco} onChange={(e) => setNovoCliente((p) => ({ ...p, endereco: e.target.value }))} />
                <input placeholder="Número" value={novoCliente.numero} onChange={(e) => setNovoCliente((p) => ({ ...p, numero: e.target.value }))} />
                <input placeholder="Bairro" value={novoCliente.bairro} onChange={(e) => setNovoCliente((p) => ({ ...p, bairro: e.target.value }))} />
                <input placeholder="Cidade" value={novoCliente.cidade} onChange={(e) => setNovoCliente((p) => ({ ...p, cidade: e.target.value }))} />
                <input placeholder="Celular" value={novoCliente.cel} onChange={(e) => setNovoCliente((p) => ({ ...p, cel: e.target.value }))} />
                <div className="aprazo-form-actions">
                  <button type="submit" className="btn-submit" disabled={saving}>
                    {editingClienteId ? 'Salvar edição' : 'Salvar cliente'}
                  </button>
                  {editingClienteId && (
                    <button type="button" className="btn-voltar-venda" onClick={cancelarEdicaoCliente}>
                      Cancelar edição
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="aprazo-card">
              <h3>Consultar clientes cadastrados</h3>
              <div className="aprazo-clientes-lista">
                {clientes.map((c) => (
                  <div
                    key={c.id}
                    className={`aprazo-cliente-item ${Number(clienteSelecionadoId) === Number(c.id) ? 'selected' : ''}`}
                    onClick={() => setClienteSelecionadoId(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setClienteSelecionadoId(c.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <strong>{c.nome}</strong>
                    <span><strong>Cel:</strong> {c.cel || 'Sem celular'}</span>
                    <span><strong>Cidade:</strong> {c.cidade || 'Sem cidade'}</span>
                    <span><strong>Bairro:</strong> {c.bairro || 'Sem bairro'}</span>
                    <span>
                      <strong>Endereco:</strong> {c.endereco || 'Sem endereco'}
                      {c.numero ? `, ${c.numero}` : ''}
                    </span>
                    <button
                      type="button"
                      className="btn-continuar"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClienteSelecionadoId(c.id);
                        iniciarEdicaoCliente(c);
                      }}
                    >
                      Editar cadastro
                    </button>
                  </div>
                ))}
                {clientes.length === 0 && <p className="venda-lista-vazia">Nenhum cliente a prazo cadastrado.</p>}
              </div>
            </div>
          </div>

          <div className="aprazo-card">
            <h3>Nova venda a prazo</h3>
            <p><strong>Cliente:</strong> {clienteSelecionado ? clienteSelecionado.nome : 'Selecione um cliente acima'}</p>
            <div className="venda-filtros">
              <select className="venda-filtro-categoria" value={categoriaFiltro} onChange={(e) => setCategoriaFiltro(e.target.value)}>
                <option value="">Todas as categorias</option>
                {categorias.map((cat) => <option key={cat.id} value={cat.id}>{cat.nome}</option>)}
              </select>
              <input
                className="venda-filtro-busca"
                placeholder="Buscar produto..."
                value={buscaProduto}
                onChange={(e) => setBuscaProduto(e.target.value)}
              />
              <select className="venda-filtro-categoria" value={metodoVenda} onChange={(e) => setMetodoVenda(e.target.value)}>
                <option value="prazo">Prazo</option>
                <option value="pix">Pix</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="debito">Débito</option>
                <option value="credito">Crédito</option>
              </select>
            </div>

            <div className="aprazo-nova-venda-montagem">
              <div className="venda-carrinho-layout">
                <div>
                  <p className="aprazo-montagem-etapa">
                    <span className="aprazo-montagem-etapa-num">1</span>
                    {pedidoFechado ? (
                      <>
                        Pedido fechado — os botões <strong>Adicionar</strong> estão desativados para esta venda. Use <strong>Reabrir pedido</strong> para incluir mais produtos.
                      </>
                    ) : (
                      <>
                        Inclua produtos na venda — use <strong>Adicionar</strong> em cada item.
                      </>
                    )}
                  </p>
                  <div className={`venda-produtos-grid${pedidoFechado ? ' venda-produtos-grid-bloqueado' : ''}`}>
                    {produtosFiltrados.map((p) => (
                      <div
                        key={p.id}
                        className={`venda-produto-card${pedidoFechado ? ' venda-produto-card--pedido-fechado' : ''}`}
                      >
                        <div className="venda-produto-card-img">
                          <img src={getImageUrl((p.fotos && p.fotos[0]) || p.foto)} alt={p.nome} />
                        </div>
                        <div className="venda-produto-card-body">
                          <span className="venda-produto-card-nome">{p.nome}</span>
                          <span className="venda-produto-card-preco">{formatCurrencyBRL(p.preco)}</span>
                          <div className="venda-produto-card-add">
                            <input
                              type="number"
                              min="1"
                              value={qtyMap[String(p.id)] ?? 1}
                              onChange={(e) => setQtyMap((prev) => ({ ...prev, [String(p.id)]: e.target.value }))}
                              disabled={pedidoFechado}
                              tabIndex={pedidoFechado ? -1 : 0}
                            />
                            <button
                              type="button"
                              className="btn-add-cart"
                              onClick={() => addCarrinho(p)}
                              disabled={pedidoFechado}
                              tabIndex={pedidoFechado ? -1 : 0}
                              aria-label={pedidoFechado ? 'Adicionar desativado — pedido fechado' : `Adicionar ${p.nome} ao carrinho`}
                            >
                              Adicionar
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="venda-carrinho-resumo">
                  <h3>Carrinho ({carrinho.length})</h3>
                  {pedidoFechado && carrinho.length > 0 && (
                    <p className="aprazo-pedido-fechado-msg">Pedido fechado — não é possível incluir novos produtos.</p>
                  )}
                  {carrinho.length === 0 ? (
                    <p className="venda-carrinho-vazio">Nenhum item ainda. Os totais e o desconto desta venda ficam na área abaixo.</p>
                  ) : (
                    <ul className="venda-carrinho-itens">
                      {carrinhoComIndicesOrdenados.map(({ item, idx }) => (
                        <li key={`${item.produto_id}-${idx}`} className="venda-carrinho-item">
                          <div className="venda-carrinho-item-info">
                            <span className="venda-carrinho-item-nome">{item.nome_produto}</span>
                            <span className="venda-carrinho-item-preco">{formatCurrencyBRL(item.preco_unitario)} un.</span>
                            <div className="venda-carrinho-item-qty">
                              <input
                                type="number"
                                min="1"
                                value={item.quantidade}
                                onChange={(e) => updateQtdCarrinho(idx, e.target.value)}
                                disabled={pedidoFechado}
                              />
                            </div>
                            <span className="venda-carrinho-item-subtotal">{formatCurrencyBRL(item.preco_unitario * item.quantidade)}</span>
                          </div>
                          <button
                            type="button"
                            className="btn-remove-cart"
                            onClick={() => removerCarrinho(idx)}
                            disabled={pedidoFechado}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <section className="aprazo-venda-fechamento" aria-label="Desconto e fechamento desta venda">
                <p className="aprazo-montagem-etapa aprazo-montagem-etapa--fechamento">
                  <span className="aprazo-montagem-etapa-num">2</span>
                  Esta área refere-se à <strong>mesma venda</strong>: desconto sobre os itens acima, depois feche o pedido e registre.
                </p>
                <div className="aprazo-desconto-bloco aprazo-desconto-bloco--venda-atual">
                  <span className="aprazo-desconto-titulo">Desconto desta venda</span>
                  <div className="aprazo-desconto-radios" role="group" aria-label="Tipo de desconto">
                    <label className="aprazo-desconto-radio">
                      <input
                        type="radio"
                        name="desconto-modo-nova-venda"
                        checked={descontoModo === 'percentual'}
                        onChange={() => setDescontoModo('percentual')}
                        disabled={pedidoFechado || carrinho.length === 0}
                      />
                      Por cento (%)
                    </label>
                    <label className="aprazo-desconto-radio">
                      <input
                        type="radio"
                        name="desconto-modo-nova-venda"
                        checked={descontoModo === 'dinheiro'}
                        onChange={() => setDescontoModo('dinheiro')}
                        disabled={pedidoFechado || carrinho.length === 0}
                      />
                      Em dinheiro (R$)
                    </label>
                  </div>
                  <div className="aprazo-desconto-linha-valor">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="aprazo-desconto-input"
                      placeholder={descontoModo === 'percentual' ? 'Ex.: 10' : 'Ex.: 5,50'}
                      value={descontoCampo}
                      onChange={(e) => setDescontoCampo(e.target.value)}
                      disabled={pedidoFechado || carrinho.length === 0}
                    />
                    <div className="aprazo-totais-ao-lado">
                      <span className="aprazo-subtotal-linha">
                        Itens: <strong>{formatCurrencyBRL(subtotalBruto)}</strong>
                      </span>
                      <span className="aprazo-desconto-linha">
                        Desconto: <strong>−{formatCurrencyBRL(valorDescontoCalculado)}</strong>
                      </span>
                      <span className="aprazo-total-final-linha">
                        Total: <strong>{formatCurrencyBRL(totalComDesconto)}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <p className="aprazo-registrar-explica">
                  <strong>Fechar pedido</strong> só impede incluir ou alterar itens. Para <strong>gravar no banco</strong> os valores (itens, desconto e total), use <strong>Registrar venda</strong>.
                </p>
                <div className="aprazo-fechar-pedido-acoes">
                  {!pedidoFechado ? (
                    <button
                      type="button"
                      className="btn-continuar"
                      onClick={() => setPedidoFechado(true)}
                      disabled={saving || carrinho.length === 0}
                    >
                      Fechar pedido
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-voltar-venda"
                      onClick={() => setPedidoFechado(false)}
                      disabled={saving}
                    >
                      Reabrir pedido
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-confirmar-venda"
                    onClick={handleCriarVenda}
                    disabled={saving || carrinho.length === 0 || totalComDesconto < 0.01}
                  >
                    Registrar venda
                  </button>
                </div>
              </section>
            </div>
          </div>

          <div className="aprazo-card">
            <h3>Vendas e pagamentos {clienteSelecionado ? `- ${clienteSelecionado.nome}` : ''}</h3>
            {vendasCliente.length === 0 ? (
              <p className="venda-lista-vazia">Nenhuma venda para o cliente selecionado.</p>
            ) : (
              <div className="aprazo-vendas-lista">
                {vendasCliente.map((venda) => {
                  const form = pagamentoForm[String(venda.id)] || {
                    valor: '',
                    metodo: 'pix',
                    data_pagamento: new Date().toISOString().slice(0, 10),
                    observacao: ''
                  };
                  const addForm = addItemForm[String(venda.id)] || { produto_id: '', quantidade: 1 };
                  const vendaAberta = venda.status === 'aberta';
                  const somaItensVenda = (venda.itens || []).reduce(
                    (s, i) => s + Number(i.subtotal || 0),
                    0
                  );
                  const brutoVenda =
                    venda.subtotal_bruto != null && venda.subtotal_bruto !== ''
                      ? Number(venda.subtotal_bruto)
                      : somaItensVenda;
                  const valorDescVenda = Number(venda.valor_desconto ?? venda.valorDesconto ?? 0);
                  return (
                    <div key={venda.id} className="vendas-pedido-card">
                      <div className="vendas-pedido-header">
                        <span>Venda #{venda.id}</span>
                        <span className={`vendas-pedido-status status-${venda.status === 'quitada' ? 'confirmado' : 'pendente'}`}>
                          {venda.status === 'quitada' ? 'Quitada' : 'Em aberto'}
                        </span>
                        <div className="aprazo-venda-acoes">
                          {venda.status === 'quitada' && (
                            <button
                              type="button"
                              className="btn-continuar"
                              onClick={() => handleReabrirVenda(venda.id)}
                              disabled={saving}
                            >
                              Reabrir venda
                            </button>
                          )}
                          <div className="aprazo-venda-acoes-stack">
                            <button
                              type="button"
                              className="btn-aprazo-excluir-venda"
                              onClick={() => handleExcluirVenda(venda.id)}
                              disabled={saving}
                            >
                              Excluir venda
                            </button>
                            <button
                              type="button"
                              className="btn-aprazo-gerar-nota"
                              onClick={() => setNotaPreviewVenda(venda)}
                            >
                              Gerar nota
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="vendas-pedido-detalhes vendas-pedido-detalhes--financeiro">
                        <span>Valor dos itens: {formatCurrencyBRL(brutoVenda)}</span>
                        <span>Desconto: −{formatCurrencyBRL(valorDescVenda)}</span>
                        <span className="vendas-pedido-total-venda">Total da venda: {formatCurrencyBRL(venda.total)}</span>
                        <span>Pago: {formatCurrencyBRL(venda.pago_total)}</span>
                        <span className="vendas-pedido-a-pagar">A pagar (saldo): {formatCurrencyBRL(venda.saldo)}</span>
                      </div>
                      <ul className="vendas-pedido-itens">
                        {(venda.itens || [])
                          .slice()
                          .sort((x, y) => ordenarNomeProdutoPt(x.nome_produto, y.nome_produto))
                          .map((item) => (
                            <li key={item.id}>{item.nome_produto} x {item.quantidade} ({formatCurrencyBRL(item.subtotal)})</li>
                          ))}
                      </ul>

                      {vendaAberta && (
                        <div className="aprazo-add-item-na-venda">
                          <span className="aprazo-add-item-label">Incluir item antes de quitar</span>
                          <select
                            className="aprazo-add-item-select"
                            value={addForm.produto_id === '' ? '' : String(addForm.produto_id)}
                            onChange={(e) =>
                              setAddItemForm((prev) => ({
                                ...prev,
                                [String(venda.id)]: {
                                  ...(prev[String(venda.id)] || { produto_id: '', quantidade: 1 }),
                                  produto_id: e.target.value === '' ? '' : Number(e.target.value)
                                }
                              }))
                            }
                          >
                            <option value="">Produto…</option>
                            {produtosAtivosOrdenados.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.nome} ({formatCurrencyBRL(p.preco)})
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min="1"
                            className="aprazo-add-item-qty"
                            value={addForm.quantidade}
                            onChange={(e) =>
                              setAddItemForm((prev) => ({
                                ...prev,
                                [String(venda.id)]: {
                                  ...(prev[String(venda.id)] || { produto_id: '', quantidade: 1 }),
                                  quantidade: e.target.value
                                }
                              }))
                            }
                          />
                          <button
                            type="button"
                            className="btn-add-cart"
                            onClick={() => handleAddItemNaVenda(venda.id)}
                            disabled={saving}
                          >
                            Adicionar à venda
                          </button>
                        </div>
                      )}

                      <div className="aprazo-pagamento-form">
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="Valor do pagamento"
                          value={form.valor}
                          onChange={(e) => setPagamentoForm((prev) => ({ ...prev, [String(venda.id)]: { ...form, valor: e.target.value } }))}
                          disabled={Number(venda.saldo) <= 0}
                        />
                        <select
                          value={form.metodo}
                          onChange={(e) => setPagamentoForm((prev) => ({ ...prev, [String(venda.id)]: { ...form, metodo: e.target.value } }))}
                          disabled={Number(venda.saldo) <= 0}
                        >
                          {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <input
                          type="date"
                          value={form.data_pagamento}
                          onChange={(e) => setPagamentoForm((prev) => ({ ...prev, [String(venda.id)]: { ...form, data_pagamento: e.target.value } }))}
                          disabled={Number(venda.saldo) <= 0}
                        />
                        <button
                          type="button"
                          className="btn-aprovar"
                          onClick={() => handleLancarPagamento(venda.id)}
                          disabled={saving || Number(venda.saldo) <= 0}
                        >
                          Lançar pagamento
                        </button>
                      </div>

                      {(venda.pagamentos || []).length > 0 && (
                        <>
                          <h5>Pagamentos lançados</h5>
                          <ul className="vendas-pedido-itens">
                            {venda.pagamentos.map((p) => (
                              <li key={p.id}>
                                {new Date(`${p.data_pagamento}T00:00:00`).toLocaleDateString('pt-BR')} - {p.metodo} - {formatCurrencyBRL(p.valor)}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
